import { motion } from "framer-motion";
import { ArrowRight } from "lucide-react";
import { LOGO_URL } from "../data/content";
import { useLang } from "../i18n/LanguageContext";

const Hero = () => {
  const { t } = useLang();
  const HERO = t.hero;

  const handleNav = (id) => {
    const el = document.getElementById(id);
    if (el) el.scrollIntoView({ behavior: "smooth", block: "start" });
  };

  return (
    <section
      id="hero"
      data-testid="hero-section"
      className="relative pt-28 pb-10 md:pt-32 md:pb-14 bg-navy-50/40 overflow-hidden"
    >
      <div className="absolute inset-0 grain pointer-events-none" />
      <div className="absolute top-1/2 -right-32 w-[480px] h-[480px] rounded-full bg-coral/5 blur-3xl pointer-events-none" />

      <div className="container-vel relative grid grid-cols-1 lg:grid-cols-12 gap-12 lg:gap-16 items-center">
        <motion.div
          initial={{ opacity: 0, y: 24 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.8, ease: "easeOut" }}
          className="lg:col-span-7"
        >
          <div className="overline mb-4" data-testid="hero-overline">
            {HERO.overline}
          </div>

          <h1
            data-testid="hero-headline"
            className="font-heading text-4xl sm:text-5xl lg:text-6xl text-navy font-light leading-[1.04] tracking-tight"
          >
            {HERO.headlinePre}{" "}
            <span className="relative inline-block">
              <span className="relative z-10 italic font-normal">
                {HERO.headlineHighlight}
              </span>
              <span className="absolute left-0 right-0 bottom-1 h-2 bg-coral/30 -z-0" />
            </span>{" "}
            {HERO.headlinePost}
          </h1>

          <p
            data-testid="hero-subheadline"
            className="mt-4 font-heading text-xl sm:text-2xl text-navy/80 leading-snug font-light italic"
          >
            {HERO.subheadline}
          </p>

          <p
            data-testid="hero-subtext"
            className="mt-4 max-w-xl font-body text-sm sm:text-base text-ink-muted leading-relaxed font-light"
          >
            {HERO.supporting}
          </p>

          <div className="mt-6 flex flex-col sm:flex-row gap-4 items-start sm:items-center">
            <button
              data-testid="hero-primary-cta"
              onClick={() => handleNav("contact")}
              className="group inline-flex items-center gap-2 bg-coral hover:bg-coral-600 text-white font-body text-sm font-medium tracking-wide px-6 py-3 rounded-sm transition-all duration-300 hover:-translate-y-0.5"
            >
              {HERO.primaryCta}
              <ArrowRight
                size={16}
                strokeWidth={1.75}
                className="transition-transform duration-300 group-hover:translate-x-1"
              />
            </button>
            <button
              data-testid="hero-secondary-cta"
              onClick={() => handleNav("program")}
              className="inline-flex items-center gap-2 border border-navy/20 hover:border-navy text-navy font-body text-sm font-medium tracking-wide px-6 py-3 rounded-sm transition-all duration-300"
            >
              {HERO.secondaryCta}
            </button>
          </div>

          <div
            data-testid="hero-metrics"
            className="mt-8 grid grid-cols-3 gap-6 max-w-xl border-t border-navy/10 pt-6"
          >
            {HERO.metrics.map((m) => (
              <div key={m.label}>
                <div className="font-heading text-2xl sm:text-3xl text-navy font-medium leading-none">
                  {m.value}
                </div>
                <div className="mt-2 font-body text-xs text-ink-soft leading-snug">
                  {m.label}
                </div>
              </div>
            ))}
          </div>
        </motion.div>

        <motion.div
          initial={{ opacity: 0, scale: 0.97 }}
          animate={{ opacity: 1, scale: 1 }}
          transition={{ duration: 1, ease: "easeOut", delay: 0.15 }}
          className="lg:col-span-5 relative"
        >
          <div className="relative aspect-[4/5] w-full max-w-[480px] mx-auto">
            <div className="absolute -top-4 -left-4 w-24 h-24 border border-coral hidden lg:block" />
            <div className="absolute -bottom-4 -right-4 w-24 h-24 border border-navy/30 hidden lg:block" />
            <img
              data-testid="hero-image"
              src="https://customer-assets.emergentagent.com/job_startup-growth-4/artifacts/7qtdvjf0_lucid-origin_a_modern_business_accelerator_company_having_office_in_a_sleek_glass_and_steel_s-0.jpg"
              alt="Modern glass-and-steel skyscraper representing scale and discipline"
              className="relative w-full h-full object-cover grayscale-[20%]"
            />
            <img
              data-testid="hero-image-watermark"
              src={LOGO_URL}
              alt=""
              aria-hidden="true"
              className="pointer-events-none absolute top-4 right-4 w-20 sm:w-24 lg:w-28 h-auto opacity-70 mix-blend-luminosity drop-shadow-lg select-none"
            />
            <div className="absolute bottom-6 left-6 right-6 bg-white/95 backdrop-blur-sm border border-navy/10 p-5">
              <div className="overline text-[10px] mb-2">
                {HERO.promiseOverline}
              </div>
              <p className="font-heading text-lg text-navy leading-snug">
                {HERO.promiseTagline}
              </p>
            </div>
          </div>
        </motion.div>
      </div>
    </section>
  );
};

export default Hero;
